import { api } from '@/api'
import type { AllowedScopesRequestBody, AllowedScopesResponse } from '@/api/access.types'
import type { OAuthClientResponse } from '@/api/oauthClients.types'
import type { AllowedScope, OAuthClient, OAuthClientInput } from '../types'
import { toAllowedScopes, toOAuthClient, toRegisterRequest, toUpdateRequest } from '../transform/oauthClientTransformer'

// The IDP admin OAuth clients API, reached through the BFF proxy. Domain types
// out; the wire shape is contained at this boundary.
const BASE = '/v1/admin/oauthclients'

// GET /v1/admin/oauthclients → OAuthClientResponse[].
export function listOAuthClients(): Promise<OAuthClient[]> {
  return api.get<OAuthClientResponse[]>(BASE).then((clients) => clients.map(toOAuthClient))
}

// POST /v1/admin/oauthclients → OAuthClientResponse (carries the one-time secret).
export function registerOAuthClient(input: OAuthClientInput): Promise<OAuthClient> {
  return api.post<OAuthClientResponse>(BASE, { body: toRegisterRequest(input) }).then(toOAuthClient)
}

// PUT /v1/admin/oauthclients/{id} → OAuthClientResponse.
export function updateOAuthClient(id: string, input: OAuthClientInput): Promise<OAuthClient> {
  return api.put<OAuthClientResponse>(`${BASE}/${id}`, { body: toUpdateRequest(input) }).then(toOAuthClient)
}

// DELETE /v1/admin/oauthclients/{id}.
export function deleteOAuthClient(id: string): Promise<void> {
  return api.delete(`${BASE}/${id}`).then(() => undefined)
}

// PUT /v1/admin/oauthclients/{id}/status.
export function setOAuthClientActive(id: string, active: boolean): Promise<void> {
  return api.put(`${BASE}/${id}/status`, { body: { value: active } }).then(() => undefined)
}

// GET /v1/admin/oauthclients/{id}/allowed-scopes → the client's mint ceiling,
// empty when uncapped.
export function getOAuthClientAllowedScopes(id: string): Promise<AllowedScope[]> {
  return api.get<AllowedScopesResponse>(`${BASE}/${id}/allowed-scopes`).then(toAllowedScopes)
}

// PUT /v1/admin/oauthclients/{id}/allowed-scopes — full replace. A
// source='yaml' (seeder-owned) client 409s here; the caller surfaces that
// inline rather than treating it as an unexpected failure.
export function putOAuthClientAllowedScopes(id: string, scopeIds: string[]): Promise<AllowedScope[]> {
  const body: AllowedScopesRequestBody = { scopeIds }
  return api.put<AllowedScopesResponse>(`${BASE}/${id}/allowed-scopes`, { body }).then(toAllowedScopes)
}
